import {Component, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {AuthService} from "src/app/services/auth.service";
import {PerformaModulesService} from "src/app/services/performa-modules.service";
import {ModuleDto} from "src/app/services/pfapi.service";

interface ConfigTile {
    title: string;
    description: string;
    icon: string;
    link: string;
    visible: boolean;
}

// landing page for the Settings area — ports the legacy Configure/Index "admintile" card grid.
// tiles are gated by permission (admin / scan-admin / PTP-admin) like the shell nav.
@Component({
    selector: "app-configure-landing",
    templateUrl: "./configure-landing.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConfigureLandingComponent implements OnInit {
    constructor(
        private readonly authService: AuthService,
        private readonly modulesService: PerformaModulesService
    ) {}

    tiles: ConfigTile[] = [];

    async ngOnInit(): Promise<void> {
        const modules = ((await this.modulesService.Modules()) || []) as ModuleDto[];
        const enabled = new Set(modules.filter(m => m.IsEnabled).map(m => m.Name));

        const isAdmin = this.authService.isAdmin;
        const isScanAdmin = this.authService.isScanAdmin;
        const isPTPAdmin = this.authService.isPTPAdmin;
        const isAPCheckAdmin = this.authService.isAPCheckAdmin;

        const all: ConfigTile[] = [
            {
                title: "Users",
                description: "Manage users.",
                icon: "fa-solid fa-users",
                link: "/configure/users",
                visible: isAdmin
            },
            {
                title: "Groups",
                description: "Manage groups & users.",
                icon: "fa-solid fa-user-group",
                link: "/configure/groups",
                visible: isAdmin
            },
            {
                title: "Document Types",
                description: "Manage document types.",
                icon: "fa-solid fa-file-lines",
                link: "/configure/documenttypes",
                visible: isAdmin
            },
            {
                title: "Keywords",
                description: "Manage system keywords.",
                icon: "fa-solid fa-tags",
                link: "/configure/keywords",
                visible: isAdmin
            },
            {
                title: "Scan Directories",
                description: "Manage scan directories.",
                icon: "fa-solid fa-folder-open",
                link: "/configure/scandirectory",
                visible: isAdmin || isScanAdmin
            },
            {
                title: "Index",
                description: "Manage search index.",
                icon: "fa-solid fa-magnifying-glass",
                link: "/configure/manageindex",
                visible: isAdmin || isScanAdmin
            },
            {
                title: "Zonal OCR",
                description: "Manage zonal templates.",
                icon: "fa-solid fa-vector-square",
                link: "/configure/zonaltemplate",
                visible: isAdmin || isScanAdmin
            },
            {
                title: "Purchase To Pay",
                description: "Manage PTP steps.",
                icon: "fa-solid fa-money-check-dollar",
                link: "/configure/ptpstep",
                visible: (isAdmin || isPTPAdmin) && enabled.has("PTP")
            },
            {
                title: "PTP Departments",
                description: "Manage PTP departments.",
                icon: "fa-solid fa-sitemap",
                link: "/configure/ptpdepartment",
                visible: (isAdmin || isPTPAdmin) && enabled.has("PTP")
            },
            {
                title: "Internal Settings",
                description: "Manage internal settings.",
                icon: "fa-solid fa-database",
                link: "/configure/internalsetting",
                visible: isAdmin
            },
            {
                title: "AP-Check Settings",
                description: "Manage AP-Check settings.",
                icon: "fa-solid fa-money-check",
                link: "/configure/apchecksetting",
                visible: (isAdmin || isAPCheckAdmin) && enabled.has("APCheck")
            },
            {
                title: "Logs",
                description: "View system logs.",
                icon: "fa-solid fa-list",
                link: "/configure/logs",
                visible: isAdmin
            },
            {
                title: "AI Settings",
                description: "Manage AI settings.",
                icon: "fa-solid fa-microchip",
                link: "/configure/aisetting",
                visible: isAdmin
            }
        ];

        this.tiles = all.filter(t => t.visible);
    }
}
